import React, { useState } from 'react';
import { format, startOfWeek, addDays, isSameDay, setHours, setMinutes } from 'date-fns';
import { Clock, Edit, X, Plus } from 'lucide-react';

export default function JobScheduler({ shifts = [], hourlyRate = 0, onAddSchedule, onEditShift, onDeleteShift, showControls = true }) {
  // Quick shift input state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickshift, setQuickshift] = useState({
    dayIndex: 0,
    startHour: 9,
    endHour: 17
  });

    // Get this week's dates starting from Monday
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Hour options for quick input (full hours only)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  
  // Calculate statistics
  const totalShifts = shifts.length;
  const totalHours = shifts.reduce((total, shift) => {
    const start = new Date(shift.start_time);
    const end = new Date(shift.end_time);
    const hours = (end - start) / (1000 * 60 * 60);
    return total + hours;
  }, 0);
  const estimatedEarnings = totalHours * hourlyRate;

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'h:mm a');
  };

  const formatHour = (hour) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return format(date, 'h:mm a');
  };

  const getShiftsForDay = (date) => {
    return shifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      return isSameDay(shiftDate, date);
    });
  };

  const handleQuickAddShift = () => {
    if (quickshift.startHour >= quickshift.endHour) {
      alert('End time must be after start time');
      return;
    }

    const selectedDate = weekDays[quickshift.dayIndex];
    const startTime = setMinutes(setHours(selectedDate, quickshift.startHour), 0);
    const endTime = setMinutes(setHours(selectedDate, quickshift.endHour), 0);

    const newShift = {
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      day_of_week: quickshift.dayIndex,
      temp_id: Date.now() // Temporary ID for display
    };

    if (onAddSchedule) {
      onAddSchedule(newShift);
    }

    // Reset form
    setQuickshift({
      dayIndex: 0,
      startHour: 9,
      endHour: 17
    });
    setShowQuickAdd(false);
  };

  return (
    <div className="space-y-4">
      {/* Statistics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-teal-600">{totalShifts}</div>
          <div className="text-xs text-stone-500">shifts scheduled</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-teal-600">{totalHours.toFixed(1)}</div>
          <div className="text-xs text-stone-500">hours total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-teal-600">{formatMoney(estimatedEarnings)}</div>
          <div className="text-xs text-stone-500">estimated</div>
        </div>
      </div>

      {/* Week Header with Quick Add */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-stone-600 font-medium">
          This Week • {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </div>
        {showControls && (
          <button
            onClick={() => setShowQuickAdd(!showQuickAdd)}
            className="flex items-center gap-1 text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-md transition-colors"
          >
            <Plus className="w-3 h-3" />
            Quick Add
          </button>
        )}
      </div>

      {/* Quick Add Form */}
      {showQuickAdd && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-4 gap-2 items-end">
            <div>
              <label className="block text-xs text-stone-600 mb-1">Day</label>
              <select
                value={quickshift.dayIndex}
                onChange={(e) => setQuickshift({...quickshift, dayIndex: parseInt(e.target.value)})}
                className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {dayLabels.map((day, index) => (
                  <option key={index} value={index}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Start</label>
              <select
                value={quickshift.startHour}
                onChange={(e) => setQuickshift({...quickshift, startHour: parseInt(e.target.value)})}
                className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {hourOptions.map(hour => (
                  <option key={hour} value={hour}>{formatHour(hour)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">End</label>
              <select
                value={quickshift.endHour}
                onChange={(e) => setQuickshift({...quickshift, endHour: parseInt(e.target.value)})}
                className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {hourOptions.map(hour => (
                  <option key={hour} value={hour}>{formatHour(hour)}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-1">
              <button
                onClick={handleQuickAddShift}
                className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => setShowQuickAdd(false)}
                className="text-stone-500 hover:text-stone-700 px-2 py-1.5 text-xs transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Grid */}
      <div className="space-y-2">
        {weekDays.map((date, index) => {
          const dayShifts = getShiftsForDay(date);
          const isToday = isSameDay(date, new Date());
          
          return (
            <div 
              key={index} 
              className={`border rounded-lg p-3 transition-colors ${
                isToday ? 'border-teal-300 bg-teal-50' : 'border-stone-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`font-medium text-sm ${isToday ? 'text-teal-700' : 'text-slate-900'}`}>
                      {dayLabels[index]}
                    </div>
                    <div className={`text-xs ${isToday ? 'text-teal-600' : 'text-stone-500'}`}>
                      {format(date, 'MMM d')}
                    </div>
                    {isToday && (
                      <span className="text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full">Today</span>
                    )}
                  </div>
                  
                  {dayShifts.length > 0 ? (
                    <div className="space-y-1">
                      {dayShifts.map((shift, shiftIndex) => (
                        <div key={shift.id || shift.temp_id || shiftIndex} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-stone-400" />
                            <span className="text-sm text-slate-700">
                              {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                            </span>
                          </div>
                          {showControls && onEditShift && onDeleteShift && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => onEditShift(shift)}
                                className="p-1 text-stone-400 hover:text-teal-600 transition-colors"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => onDeleteShift(shift.id)}
                                className="p-1 text-stone-400 hover:text-red-600 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-stone-400">No shifts scheduled</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Next Week Preview */}
      {shifts.length === 0 && (
        <div className="text-center py-8">
          <div className="text-3xl mb-3 opacity-50">📅</div>
          <h4 className="text-base font-semibold text-slate-900 mb-1">No shifts scheduled</h4>
          <p className="text-stone-500 text-sm mb-4">Add your work schedule to track hours and earnings</p>
          {showControls && onAddSchedule && (
            <button 
              onClick={onAddSchedule}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Add Work Schedule
            </button>
          )}
        </div>
      )}
    </div>
  );
}